import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, Check, Cloud, Brain, Cog, Globe2, Building, Blend, Users,
  Trophy, ShieldCheck, Zap, Scaling, Target, Sparkles, ShoppingBag, Landmark, Building2,
  HeartPulse, Factory, Radio, Truck, Film, UserPlus
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScheduleConsultationSection from '../components/ScheduleConsultationSection';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  staffCategories, engagementModels, staffProcess, staffWhyPartner,
  staffBenefits, staffIndustries, staffFaqs
} from '../data/staffAugmentation';

const iconMap = {
  Cloud, Brain, Cog, Globe2, Building, Blend, Users,
  Trophy, ShieldCheck, Zap, Scaling, Target, Sparkles,
  ShoppingBag, Landmark, Building2, HeartPulse, Factory, Radio, Truck, Film
};

const HERO_IMAGE = 'https://images.unsplash.com/photo-1571171637578-41bc2dd41cd2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxyZW1vdGUlMjBkZXZlbG9wZXJzfGVufDB8fHx8MTc4NDcwOTUxM3ww&ixlib=rb-4.1.0&q=85&w=1800';
const CODE_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxlbmdpbmVlcnMlMjBjb2Rpbmd8ZW58MHx8fHwxNzg0NzA5NDk4fDA&ixlib=rb-4.1.0&q=85&w=1400';
const TEAM_IMAGE = 'https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxkZXZlbG9wZXIlMjB0ZWFtfGVufDB8fHx8MTc4NDcwOTQ5OHww&ixlib=rb-4.1.0&q=85&w=1400';

const StaffAugmentationPage = () => {
  return (
    <main className="bg-[#05080f] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Global tech team" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05080f]/70 via-[#05080f]/85 to-[#05080f]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.18),transparent_60%)]"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-300 text-sm mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="section-label mb-6">Staff Augmentation Services</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.03] tracking-tight text-white max-w-4xl">
            Scale with <span className="gradient-text">world-class</span> Cloud, AI & DevOps experts
          </h1>
          <p className="mt-6 text-slate-300 max-w-2xl text-base md:text-lg leading-relaxed">
            The right talent. The right skills. Delivered when you need them.
            Onsite, remote, or hybrid — professionals who integrate with your team from day one.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/#contact">
              <Button className="btn-gradient rounded-full h-11 px-6 border-0">
                Request qualified talent today <ArrowUpRight size={16} className="ml-1" />
              </Button>
            </Link>
            <a href="#roles">
              <Button variant="outline" className="rounded-full h-11 px-6 bg-white/5 border-white/10 text-white hover:bg-white/10">
                Explore roles
              </Button>
            </a>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { k: 'Days', v: 'Fast', label: 'Deployment' },
              { k: '3', v: 'Cloud', label: 'Practices' },
              { k: '30+', v: 'Roles', label: 'Available' },
              { k: '3', v: 'Models', label: 'Onsite · Remote · Hybrid' }
            ].map((s) => (
              <div key={s.label} className="card-dark rounded-2xl p-4">
                <div className="font-display text-2xl gradient-text font-semibold">{s.v}</div>
                <div className="text-slate-400 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10 order-2 lg:order-1">
            <img src={CODE_IMAGE} alt="Engineers at work" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-bl from-[#05080f]/70 via-transparent to-amber-500/10"></div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="section-label mb-6">Why Staff Augmentation?</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mb-5">
              Immediate access to <span className="gradient-text">specialized expertise</span>
            </h2>
            <p className="text-slate-400 leading-relaxed">
              Traditional recruitment is lengthy, expensive, and often fails to deliver the specialized
              expertise required for today’s technology landscape.
            </p>
            <p className="text-slate-400 leading-relaxed mt-4">
              Our consultants, engineers, and architects are ready to contribute from day one —
              helping you scale faster while maintaining quality, agility, and operational excellence.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER - 3 categories */}
      <section id="roles" className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">What We Offer</div>
          <div className="grid md:grid-cols-2 gap-6 items-end mb-14">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight">
              Three practices. <br className="hidden md:block" />
              One <span className="gradient-text">trusted talent partner</span>
            </h2>
            <p className="text-slate-400 max-w-md md:mt-4">
              Cloud, AI, and DevOps professionals — pre-vetted, business-ready, and available
              in the model that works for you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {staffCategories.map((cat) => {
              const Icon = iconMap[cat.icon] || Sparkles;
              return (
                <div key={cat.id} className="group card-dark rounded-3xl p-7 relative overflow-hidden hover:border-amber-500/30 transition-colors flex flex-col">
                  <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full blur-3xl opacity-0 group-hover:opacity-30 bg-amber-500 transition-opacity duration-500"></div>
                  <div className="relative h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-5">
                    <Icon className="text-amber-400" size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="relative font-display text-white text-xl font-semibold mb-2">{cat.title}</h3>
                  <p className="relative text-amber-300 text-sm mb-5">{cat.tagline}</p>
                  <ul className="relative space-y-2 flex-1">
                    {cat.roles.map((r) => (
                      <li key={r} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check size={14} className="text-amber-400 mt-1 shrink-0" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Engagement Models</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight">
              Built around <span className="gradient-text">your project goals</span>
            </h2>
            <p className="text-slate-400 max-w-md">Choose an engagement model that fits your workflow, geography, and business rhythm.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {engagementModels.map((m) => {
              const Icon = iconMap[m.icon] || Sparkles;
              return (
                <div key={m.id} className="card-dark rounded-3xl p-6 hover:border-amber-500/30 transition-colors">
                  <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-5">
                    <Icon className="text-amber-400" size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-white text-lg font-semibold mb-2">{m.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{m.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY PARTNER */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={TEAM_IMAGE} alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05080f] via-[#05080f]/85 to-[#05080f]"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Why Organizations Partner With Us</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-14">
            Talent, delivered with <span className="gradient-text">confidence</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {staffWhyPartner.map((w) => {
              const Icon = iconMap[w.icon] || Sparkles;
              return (
                <div key={w.title} className="card-dark rounded-3xl p-7 hover:border-amber-500/30 transition-colors">
                  <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-5">
                    <Icon className="text-amber-400" size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-white text-lg font-semibold mb-2">{w.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{w.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Our Staffing Process</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight">
              Rigorous, rapid, <span className="gradient-text">right-fit</span>
            </h2>
            <p className="text-slate-400 max-w-md">Five deliberate stages that ensure we place the right person, in the right role, at the right time.</p>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"></div>
            <div className="grid md:grid-cols-5 gap-6">
              {staffProcess.map((p) => (
                <div key={p.step} className="relative card-dark rounded-2xl p-6 hover:border-amber-500/30 transition-colors">
                  <div className="relative z-10 h-11 w-11 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 font-display font-semibold flex items-center justify-center mb-4">
                    {p.step}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{p.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Why Choose Us</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mb-10">
            Everything you need to build a high-performing team
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {staffBenefits.map((b) => (
              <div key={b} className="card-dark rounded-2xl p-4 flex items-start gap-3">
                <Check size={16} className="text-amber-400 mt-0.5 shrink-0" />
                <span className="text-slate-200 text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Industries We Support</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            Delivering across <span className="gradient-text">every sector</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {staffIndustries.map((i) => {
              const Icon = iconMap[i.icon] || Sparkles;
              return (
                <div key={i.name} className="card-dark rounded-2xl p-5 flex flex-col items-center text-center hover:border-amber-500/30 transition-colors group">
                  <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-3 group-hover:bg-amber-500/15">
                    <Icon className="text-amber-400" size={20} strokeWidth={1.5} />
                  </div>
                  <span className="text-slate-200 text-sm font-medium">{i.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">FAQ</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-10">
            Common questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {staffFaqs.map((f) => (
              <AccordionItem key={f.q} value={f.q} className="card-dark rounded-2xl px-6 border !border-white/10">
                <AccordionTrigger className="text-left text-white hover:no-underline hover:text-amber-300 py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Schedule consultation */}
      <ScheduleConsultationSection
        label="Schedule a Call"
        title={<>Book a call with our <span className="gradient-text">talent team</span></>}
        subtitle="Pick a date to discuss the roles you need, engagement model, and delivery timeline."
      />

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="card-dark rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0">
              <img src={TEAM_IMAGE} alt="" className="w-full h-full object-cover opacity-15" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.18),transparent_60%)]"></div>
            </div>
            <div className="relative">
              <UserPlus className="text-amber-300 mx-auto mb-3" size={28} />
              <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
                Build stronger teams. <span className="gradient-text">Deliver better results.</span>
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                Whether you need a single specialist or an entire delivery team, we’re ready to help you
                scale with confidence.
              </p>
              <Link to="/#contact">
                <Button className="btn-gradient rounded-full h-12 px-8 border-0 text-base">
                  Find the right talent for your next project
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default StaffAugmentationPage;
